"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const ToastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-3xl border p-6 pr-8 shadow-lg transition-all " +
    "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x) " +
    "data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=move]:transition-none " +
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out " +
    "data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full",
  {
    variants: {
      variant: {
        default: "border bg-background text-foreground",
        destructive:
          "border-destructive bg-destructive text-destructive-foreground",
        success: "border-green-600 bg-green-600 text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface ToastProps extends VariantProps<typeof ToastVariants> {
  id?: string;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  duration?: number;
}

interface ToastInternal extends ToastProps {
  id: string;
  visible: boolean;
}

interface ToastContextType {
  toasts: ToastInternal[];
  addToast: (toast: ToastProps) => string;
  removeToast: (id: string) => void;
  updateToast: (id: string, props: Partial<ToastProps>) => void;
}

// on exporte bien le contexte pour que votre hook puisse l’importer
export const ToastContext = React.createContext<ToastContextType>({
  toasts: [],
  addToast: () => "",
  removeToast: () => {},
  updateToast: () => {},
});

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastInternal[]>([]);

  const addToast = React.useCallback((toast: ToastProps) => {
    const id = toast.id ?? Math.random().toString(36).substring(2, 9);
    const newToast: ToastInternal = { ...toast, id, visible: true };
    setToasts((prev) => [...prev, newToast]);

    if (toast.duration !== Number.POSITIVE_INFINITY) {
      const timeout = toast.duration ?? 5000;
      setTimeout(() => {
        setToasts((prev) =>
          prev.map((t) => (t.id === id ? { ...t, visible: false } : t))
        );
        // on retire après l’animation
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 300);
      }, timeout);
    }

    return id;
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, visible: false } : t))
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const updateToast = React.useCallback(
    (id: string, props: Partial<ToastProps>) => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, ...props } : t))
      );
    },
    []
  );

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, updateToast }}
    >
      {children}
    </ToastContext.Provider>
  );
}

/** Le « wrapper » pour afficher tous les toasts */
export function ToastViewport({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <div className="fixed top-0 z-[9999] flex flex-col items-end gap-2 px-4 pt-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col-reverse sm:pb-4">
      {children}
    </div>
  );
}

/** Composant d’un toast individuel.
 *  On s’appuie sur `children` pour passer le titre/la description + action. */
export function Toast({
  id,
  variant = "default",
  visible = true,
  children,
}: {
  id: string;
  variant?: ToastProps["variant"];
  visible?: boolean;
  children: React.ReactNode;
}) {
  const { removeToast } = React.useContext(ToastContext);
  return (
    <div
      className={cn(
        ToastVariants({ variant }),
        "max-w-md",
        !visible && "opacity-0"
      )}
    >
      {children}
      <ToastClose onClick={() => removeToast(id)} />
    </div>
  );
}

export function ToastTitle({ children }: { children: React.ReactNode }) {
  return <div className="text-sm font-semibold">{children}</div>;
}
export function ToastDescription({ children }: { children: React.ReactNode }) {
  return <div className="text-sm opacity-90">{children}</div>;
}
export function ToastClose(
  props: React.ButtonHTMLAttributes<HTMLButtonElement>
) {
  return (
    <button
      {...props}
      className={cn(
        "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-hidden focus:ring-2 group-hover:opacity-100",
        props.className
      )}
    >
      <X className="h-4 w-4" />
    </button>
  );
}